import React, { useEffect, useState } from 'react';
import Button from '../Button';
import Field from '../Field';
import { DELETION_REFUSAL, deletionBlocker, deletionMessage } from '../../domain/accountDeletion';
import c from '../ConfirmDialog.module.css';
import s from './DeleteAccountDialog.module.css';

// The last step of deleting an account (#432): what will be lost, the word,
// the password, and the button.
//
// Every sentence and every rule is the core's (core/src/account_deletion.rs),
// so this says what the phones say and refuses what they refuse. The only
// decisions made here are about the screen:
//
//  * **Offline and owed writes are said; the word and the password are not.**
//    The first two are things the teacher cannot see from the form and has to
//    be told; the last two are the form itself, and a red line under an empty
//    box they have not reached yet is nagging.
//  * **No dismiss on the scrim**, as ConfirmDialog: a stray click must not
//    close the form and lose what was typed, and must certainly not confirm.
//  * **The button is `danger` and says what it does.** A dialog about deleting
//    a studio whose primary button reads "OK" is how the wrong one gets pressed.
//
// `onDelete({ password, confirmation })` does the work and rejects with a
// `reason` from DELETION_REFUSAL; `online` and `owedWrites` come from the sync
// status. Presentational apart from its own boxes, so test/ui drives it with a
// fake `onDelete`.
//
// `reauth` is 'password' or 'google' (domain/signIn.js `reauthMethod`). An
// account with Google and no password (#567) has no password box: it signs
// in with Google again when the button is pressed, and the line where the box
// was says so. The core's blocker still asks "is a way to re-authenticate
// ready?"; for Google the answer is yes before anything is typed, so the word
// is the only thing left to wait for.
const w = deletionMessage;

export default function DeleteAccountDialog({
  open, online = true, owedWrites = 0, reauth = 'password', onDelete, onCancel, onDeleted,
}) {
  const withGoogle = reauth === 'google';
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);

  // A dialog opened again starts empty: a password left in a box between two
  // openings is a password left on the screen.
  useEffect(() => {
    if (!open) {
      setTyped('');
      setPassword('');
      setRefusal(null);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const blocker = deletionBlocker({
    online, owedWrites, typed, passwordTyped: withGoogle || password.length > 0,
  });
  const said = blocker === DELETION_REFUSAL.offline || blocker === DELETION_REFUSAL.writesOwed
    ? blocker
    : null;

  const confirm = async () => {
    if (blocker || busy) return;
    setBusy(true);
    setRefusal(null);
    try {
      await onDelete({ password, confirmation: typed });
    } catch (err) {
      setRefusal(err?.reason || DELETION_REFUSAL.failed);
      // A wrong password is typed again; everything else keeps the box as it
      // was so a retry is one click.
      if (err?.reason === DELETION_REFUSAL.wrongPassword || err?.reason === DELETION_REFUSAL.signInTooOld) {
        setPassword('');
      }
      setBusy(false);
      return;
    }
    // Left busy: the page is about to be replaced by the done screen.
    onDeleted?.();
  };

  return (
    <div className={c.scrim}>
      <div className={c.dialog} role="alertdialog" aria-modal="true" aria-label={w('title')}>
        <div className={c.title}>{w('title')}</div>
        <div className={c.message}>
          <p className={s.lead}>{w('lead')}</p>
          <p className={s.para}>{w('loses')}</p>
          <p className={s.para}>{w('billing')}</p>
        </div>

        <form
          className={s.fields}
          onSubmit={(e) => { e.preventDefault(); confirm(); }}
        >
          <Field label={w('confirmLabel')}>
            <input
              type="text"
              value={typed}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={busy}
              onChange={(e) => setTyped(e.target.value)}
            />
          </Field>
          {withGoogle ? (
            <p className={s.para}>{w('googleHint')}</p>
          ) : (
            <Field
              label={w('passwordLabel')}
              hint={w('passwordHint')}
              error={refusal === DELETION_REFUSAL.wrongPassword || refusal === DELETION_REFUSAL.signInTooOld
                ? w(refusal)
                : null}
            >
              <input
                type="password"
                value={password}
                autoComplete="current-password"
                disabled={busy}
                data-error={refusal === DELETION_REFUSAL.wrongPassword ? 'true' : undefined}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}
          {/* Submitted by Enter in either box; the visible button is below. */}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>

        {said ? <p className={s.status} role="status">{w(said)}</p> : null}
        {refusal && (withGoogle
          || (refusal !== DELETION_REFUSAL.wrongPassword && refusal !== DELETION_REFUSAL.signInTooOld)) ? (
          <p className={s.refusal} role="alert">{w(refusal)}</p>
        ) : null}

        <div className={c.foot}>
          <span className={c.spacer} />
          <Button variant="secondary" disabled={busy} onClick={onCancel}>{w('cancel')}</Button>
          <Button variant="danger" loading={busy} disabled={Boolean(blocker) || busy} onClick={confirm}>
            {busy ? w('working') : w('button')}
          </Button>
        </div>
      </div>
    </div>
  );
}
